import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const isActiveLink = (pathname, to) =>
  pathname === to || (to !== '/' && pathname.startsWith(to));

const headerBgClass = (scrolled) =>
  scrolled
    ? 'bg-[#080B10]/85 backdrop-blur-xl border-b border-white/10'
    : 'bg-transparent';

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return scrolled;
}

function LanguageDropdown({ onDarkBg }) {
  const { t, lang, setLang, SUPPORTED_LANGS } = useLanguage();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-selector"
        className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-[#6366F1] outline-none ${
          onDarkBg ? 'text-white/80' : 'text-[#0A192F]/70'
        }`}
      >
        <Globe className="h-4 w-4" />
        <span>{t(`lang.${lang}`)}</span>
        <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {SUPPORTED_LANGS.map((l) => (
          <DropdownMenuItem
            key={l}
            data-testid={`lang-option-${l}`}
            onClick={() => setLang(l)}
            className={`cursor-pointer ${lang === l ? 'text-[#6366F1] font-semibold' : ''}`}
          >
            {t(`lang.${l}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesktopNav({ navLinks, onDarkBg }) {
  const location = useLocation();
  return (
    <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
      {navLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          data-testid={`nav-link-${link.to.split('/').filter(Boolean).pop() || 'home'}`}
          className={`text-sm font-medium transition-colors hover:text-[#6366F1] ${
            isActiveLink(location.pathname, link.to)
              ? 'text-[#6366F1]'
              : onDarkBg
                ? 'text-white/80'
                : 'text-[#0A192F]/70'
          }`}
        >
          {link.label}
        </Link>
      ))}
      <LanguageDropdown onDarkBg={onDarkBg} />
    </nav>
  );
}

function MobileNav({ navLinks, isDark, onClose }) {
  const location = useLocation();
  const { t, lang, setLang, SUPPORTED_LANGS } = useLanguage();
  return (
    <nav
      data-testid="mobile-nav"
      className={`md:hidden pb-6 space-y-1 ${
        isDark ? 'bg-black/95' : 'bg-white/95'
      } backdrop-blur-xl rounded-b-2xl`}
    >
      {navLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          data-testid={`mobile-nav-link-${link.to.split('/').filter(Boolean).pop() || 'home'}`}
          className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
            location.pathname === link.to
              ? 'text-[#6366F1] bg-[#6366F1]/10'
              : isDark
                ? 'text-white/70 hover:text-white'
                : 'text-[#0A192F]/70 hover:text-[#0A192F]'
          }`}
        >
          {link.label}
        </Link>
      ))}
      <div className="px-4 pt-2 flex gap-2">
        {SUPPORTED_LANGS.map((l) => (
          <button
            key={l}
            data-testid={`mobile-lang-${l}`}
            onClick={() => { setLang(l); onClose(); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              lang === l
                ? 'bg-[#6366F1] text-white'
                : isDark
                  ? 'bg-white/10 text-white/60'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {t(`lang.${l}`)}
          </button>
        ))}
      </div>
    </nav>
  );
}

export default function Header() {
  const { t } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrolled = useScrolled();

  const isDark = true;
  const onDarkBg = true;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/products', label: t('nav.products') },
    { to: '/about', label: t('nav.about') },
    { to: '/insights', label: t('nav.blog') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBgClass(scrolled)}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="logo-link"
            className={`font-bold text-xl tracking-tight transition-colors ${onDarkBg ? 'text-white' : 'text-[#0A192F]'}`}
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            DZent
          </Link>

          <DesktopNav navLinks={navLinks} onDarkBg={onDarkBg} />

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 transition-colors ${onDarkBg ? 'text-white' : 'text-[#0A192F]'}`}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileOpen && (
          <MobileNav navLinks={navLinks} isDark={isDark} onClose={() => setMobileOpen(false)} />
        )}
      </div>
    </header>
  );
}
